import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useUnreadCount } from './useMessages';
import { subscribeToLandlordViewings } from '../services/viewings.service';

/**
 * Centralised badge counts for private landlords.
 * Both LandlordPrivateShell and WebsiteHeader consume this hook
 * so the numbers are always identical.
 */
export default function useLandlordBadges(userId) {
  const { unreadCount: unreadMessageCount } = useUnreadCount(userId);
  const [pendingViewingsCount, setPendingViewingsCount] = useState(0);
  const [pendingContractsCount, setPendingContractsCount] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  // Viewings: pending + requested that landlord hasn't read yet
  useEffect(() => {
    if (!userId) { setPendingViewingsCount(0); return; }
    return subscribeToLandlordViewings(
      userId,
      null,
      (viewings) => {
        setPendingViewingsCount(
          viewings.filter((v) =>
            ['pending', 'requested'].includes(v.status) && v.readByLandlord !== true
          ).length
        );
      },
      () => setPendingViewingsCount(0)
    );
  }, [userId]);

  const [amendmentActionCount, setAmendmentActionCount] = useState(0);

  // Contracts signed by tenant, waiting for landlord (query both field name variants)
  useEffect(() => {
    if (!userId) { setPendingContractsCount(0); return; }
    let count1 = 0;
    let count2 = 0;
    const merge = () => setPendingContractsCount(count1 + count2);

    const q1 = query(collection(db, 'contracts'), where('landlordID', '==', userId), where('status', '==', 'signedByTenant'));
    const q2 = query(collection(db, 'contracts'), where('landlordId', '==', userId), where('status', '==', 'signedByTenant'));

    const unsub1 = onSnapshot(q1, (snap) => { count1 = snap.size; merge(); }, () => { count1 = 0; merge(); });
    const unsub2 = onSnapshot(q2, (snap) => {
      const unique = snap.docs.filter(d => d.data().landlordID !== userId);
      count2 = unique.length;
      merge();
    }, () => { count2 = 0; merge(); });

    return () => { unsub1(); unsub2(); };
  }, [userId]);

  // Amendments needing landlord counter-signature
  useEffect(() => {
    if (!userId) { setAmendmentActionCount(0); return; }

    let docs1 = [];
    let docs2 = [];
    const merge = () => {
      const seen = new Set();
      const total = [...docs1, ...docs2].filter((d) => {
        if (seen.has(d.id)) return false;
        seen.add(d.id);
        return d.data().amendmentState === 'tenantAccepted';
      }).length;
      setAmendmentActionCount(total);
    };

    const q1 = query(collection(db, 'contracts'), where('landlordID', '==', userId), where('amendmentState', '==', 'tenantAccepted'));
    const q2 = query(collection(db, 'contracts'), where('landlordId', '==', userId), where('amendmentState', '==', 'tenantAccepted'));

    const unsub1 = onSnapshot(q1, (snap) => { docs1 = snap.docs; merge(); }, () => { docs1 = []; merge(); });
    const unsub2 = onSnapshot(q2, (snap) => { docs2 = snap.docs; merge(); }, () => { docs2 = []; merge(); });

    return () => { unsub1(); unsub2(); };
  }, [userId]);

  // Unread notifications (real-time) — only uses recipientUserId which is the
  // canonical field for "who should see this notification". The old q2 query on
  // landlordId caused false positives because that field is also set as metadata
  // on notifications the landlord created FOR the tenant.
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  useEffect(() => {
    if (!userId) { setUnreadNotificationsCount(0); setUnreadNotifications([]); return; }

    const q = query(collection(db, 'notifications'), where('recipientUserId', '==', userId), where('read', '==', false));

    const unsub = onSnapshot(q, (snap) => {
      const sorted = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => {
        const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return tb - ta;
      });
      setUnreadNotificationsCount(sorted.length);
      setUnreadNotifications(sorted);
    }, () => { setUnreadNotificationsCount(0); setUnreadNotifications([]); });

    return () => unsub();
  }, [userId]);

  const archivedPropertiesCount = useMemo(
    () => unreadNotifications.filter((n) => n.type === 'contract_terminated_archive').length,
    [unreadNotifications]
  );

  // Pending termination requests where the landlord is counterparty and hasn't viewed yet
  const [pendingTerminationsCount, setPendingTerminationsCount] = useState(0);
  useEffect(() => {
    if (!userId) { setPendingTerminationsCount(0); return; }
    const q = query(
      collection(db, 'terminationRequests'),
      where('counterpartyUserId', '==', userId),
      where('status', '==', 'requested')
    );
    const unsub = onSnapshot(q, (snap) => {
      const unviewed = snap.docs.filter((d) => {
        const data = d.data() || {};
        if (data.viewedByCounterparty === true) return false;
        const viewedAt = data.delivery?.inAppViewedAt;
        if (viewedAt != null) return false;
        return true;
      }).length;
      setPendingTerminationsCount(unviewed);
    }, () => setPendingTerminationsCount(0));
    return () => unsub();
  }, [userId]);

  // Property-related notification types that should badge "Verhuurde Panden"
  const rentedPandNotifCount = useMemo(
    () => unreadNotifications.filter((n) => {
      const t = n.type || '';
      return t === 'payment_marked_paid'
        || t === 'property_problem_reported'
        || t.includes('problem')
        || t === 'contract_signed'
        || t === 'contract_declined';
    }).length,
    [unreadNotifications]
  );

  // Direct listener on propertyReports for pending problems (fallback if notification wasn't created)
  const [pendingProblemsCount, setPendingProblemsCount] = useState(0);
  useEffect(() => {
    if (!userId) { setPendingProblemsCount(0); return; }
    const q = query(
      collection(db, 'propertyReports'),
      where('landlordId', '==', userId),
      where('status', '==', 'pending')
    );
    const unsub = onSnapshot(q, (snap) => setPendingProblemsCount(snap.size), () => setPendingProblemsCount(0));
    return () => unsub();
  }, [userId]);

  const getBadgeCount = useMemo(() => (path) => {
    if (path === '/landlord/messages') return unreadMessageCount || 0;
    if (path === '/landlord/viewings') return pendingViewingsCount || 0;
    if (path === '/landlord/contracts') return (pendingContractsCount || 0) + (amendmentActionCount || 0);
    if (path === '/landlord/verhuurde-panden') return (rentedPandNotifCount || 0) + (pendingTerminationsCount || 0) + (pendingProblemsCount || 0);
    if (path === '/landlord/properties') return archivedPropertiesCount || 0;
    if (path === '/landlord') return 0;
    return 0;
  }, [unreadMessageCount, pendingViewingsCount, pendingContractsCount, amendmentActionCount, rentedPandNotifCount, pendingTerminationsCount, pendingProblemsCount, archivedPropertiesCount]);

  return {
    unreadMessageCount,
    pendingViewingsCount,
    pendingContractsCount,
    amendmentActionCount,
    unreadNotificationsCount,
    archivedPropertiesCount,
    unreadNotifications,
    getBadgeCount,
  };
}
